/**
 * TrainingHistoryCard — every batch a person has been enrolled in, with the
 * trainer-controlled "current batch" pointer and enrollment status actions.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star, CheckCircle2, RotateCcw, UserMinus, FileDown } from 'lucide-react';
import api from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const STATUS_TONE = {
  active: 'bg-amber-100 text-amber-700 border-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  dropped: 'bg-slate-100 text-slate-500 border-slate-300',
};

const fmt = (iso) => (iso ? iso.slice(0, 10) : '—');

export default function TrainingHistoryCard({ traineeId, traineeName, canEdit = false, onChanged }) {
  const { get, patch, del } = useApi();
  const [rows, setRows] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/trainees/${traineeId}/history.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training-history-${(traineeName || 'trainee').replace(/[^a-zA-Z0-9-_ ]/g, '_').slice(0, 60)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Training history PDF downloaded');
    } catch { toast.error('Could not generate PDF'); }
    finally { setDownloading(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await get(`/trainees/${traineeId}/enrollments`);
      setRows(res.enrollments || []);
    } catch { setRows([]); }
  }, [get, traineeId]);

  useEffect(() => { if (traineeId) load(); }, [traineeId, load]);

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <Card data-testid="training-history-card">
      <div className="p-3 border-b flex items-center justify-between">
        <h4 className="font-display text-sm font-semibold">Training history</h4>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{rows?.length ?? 0} batch{rows?.length === 1 ? '' : 'es'}</span>
          <Button size="sm" variant="outline" onClick={downloadPdf} disabled={downloading}
                  data-testid="training-history-pdf-btn" title="Download one-page PDF for HR / client audits">
            <FileDown className="w-4 h-4 mr-1" /> {downloading ? 'Preparing…' : 'History PDF'}
          </Button>
        </div>
      </div>
      {rows === null ? (
        <p className="p-3 text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="p-3 text-sm text-muted-foreground">Not enrolled in any batch yet.</p>
      ) : (
        <div className="divide-y">
          {rows.map(e => (
            <div key={e.enrollment_id} className="flex items-center justify-between gap-3 p-3"
                 data-testid={`enrollment-row-${e.batch_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate flex items-center gap-2">
                  {e.batch_code ? `${e.batch_code} — ` : ''}{e.batch_name}
                  {e.is_current && (
                    <Badge variant="outline" className="text-[10px] gap-1" data-testid={`enrollment-current-${e.batch_id}`}>
                      <Star className="w-3 h-3" /> Current
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  Joined {fmt(e.joined_at)}
                  {e.status === 'completed' && ` · Completed ${fmt(e.completed_at)}`}
                  {e.status === 'dropped' && ` · Removed ${fmt(e.dropped_at)}`}
                  {e.department ? ` · ${e.department}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Badge variant="outline" className={STATUS_TONE[e.status] || ''}
                       data-testid={`enrollment-status-${e.batch_id}`}>
                  {e.status}
                </Badge>
                {canEdit && e.status !== 'dropped' && !e.is_current && (
                  <Button size="sm" variant="ghost" title="Make current batch"
                          data-testid={`enrollment-make-current-${e.batch_id}`}
                          onClick={() => act(() => patch(`/trainees/${traineeId}/enrollments/${e.batch_id}`, { make_current: true }), `Current batch set to ${e.batch_name}`)}>
                    <Star className="w-4 h-4" />
                  </Button>
                )}
                {canEdit && e.status === 'active' && (
                  <Button size="sm" variant="ghost" title="Mark completed"
                          data-testid={`enrollment-complete-${e.batch_id}`}
                          onClick={() => act(() => patch(`/trainees/${traineeId}/enrollments/${e.batch_id}`, { status: 'completed' }), `Marked ${e.batch_name} completed`)}>
                    <CheckCircle2 className="w-4 h-4" />
                  </Button>
                )}
                {canEdit && e.status !== 'active' && (
                  <Button size="sm" variant="ghost" title="Re-activate enrollment"
                          data-testid={`enrollment-reactivate-${e.batch_id}`}
                          onClick={() => act(() => patch(`/trainees/${traineeId}/enrollments/${e.batch_id}`, { status: 'active' }), `Re-activated in ${e.batch_name}`)}>
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
                {canEdit && e.status !== 'dropped' && (
                  <Button size="sm" variant="ghost" title="Remove from batch"
                          data-testid={`enrollment-drop-${e.batch_id}`}
                          onClick={() => act(() => del(`/trainees/${traineeId}/enrollments/${e.batch_id}`), `Removed from ${e.batch_name}`)}>
                    <UserMinus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
